// Central route map for ReadBarcode.com. Used by header, footer, and sitemap.
export type ToolRoute = {
  path: string;
  label: string;
  description: string;
  group: "reader" | "generator" | "lookup" | "validator" | "gs1" | "inventory" | "guide" | "legal";
  status: "live" | "planned";
};

export const TOOL_ROUTES: ToolRoute[] = [
  {
    path: "/read-barcode-app",
    label: "Read Barcode App",
    description: "Free installable read barcode app — scan UPC, EAN, QR and Data Matrix offline.",
    group: "reader",
    status: "live",
  },
  {
    path: "/readers",
    label: "Barcode & QR Readers",
    description: "Hub for every ReadBarcode reader — barcode, QR, Data Matrix and ISBN.",
    group: "reader",
    status: "live",
  },
  {
    path: "/barcode-reader",
    label: "Online Barcode Reader",
    description: "Scan or upload a barcode and decode it instantly.",
    group: "reader",
    status: "live",
  },
  {
    path: "/read-barcode-from-image",
    label: "Read Barcode From Image",
    description: "Upload a photo to extract the barcode value.",
    group: "reader",
    status: "live",
  },
  {
    path: "/qr-code-reader",
    label: "QR Code Reader",
    description: "Decode QR codes from camera or image.",
    group: "reader",
    status: "live",
  },
  {
    path: "/data-matrix-reader",
    label: "Data Matrix Reader",
    description: "Decode 2D Data Matrix codes used in healthcare and logistics.",
    group: "reader",
    status: "live",
  },
  {
    path: "/generators",
    label: "Barcode & QR Generators",
    description:
      "Hub for every ReadBarcode generator — barcode, QR, Wi-Fi, vCard, Data Matrix and GS1 Digital Link.",
    group: "generator",
    status: "live",
  },
  {
    path: "/barcode-generator",
    label: "Barcode Generator",
    description: "Generate UPC, EAN, Code 128, Code 39, ITF-14 and Codabar as PNG or SVG.",
    group: "generator",
    status: "live",
  },
  {
    path: "/bulk-barcode-generator",
    label: "Bulk Barcode Generator",
    description:
      "Paste a list and generate many UPC, EAN or Code 128 barcodes at once. Print or download as a sheet.",
    group: "generator",
    status: "live",
  },
  {
    path: "/qr-code-generator",
    label: "QR Code Generator",
    description: "Generate QR codes with custom error correction, size and colors as PNG or SVG.",
    group: "generator",
    status: "live",
  },
  {
    path: "/wifi-qr-code-generator",
    label: "Wi-Fi QR Code Generator",
    description:
      "Turn your Wi-Fi network into a one-scan QR code — guests join without typing the password.",
    group: "generator",
    status: "live",
  },
  {
    path: "/gs1-digital-link-generator",
    label: "GS1 Digital Link Generator",
    description:
      "Build Sunrise-2027 GS1 Digital Link URLs from GTIN + AIs and render as QR or Data Matrix.",
    group: "generator",
    status: "live",
  },
  {
    path: "/data-matrix-generator",
    label: "Data Matrix Generator",
    description: "Encode text, UDI or GS1 payloads as a Data Matrix barcode. PNG and SVG output.",
    group: "generator",
    status: "live",
  },
  {
    path: "/vcard-qr-generator",
    label: "vCard QR Code Generator",
    description: "Generate a vCard QR so anyone can save your contact details with a single scan.",
    group: "generator",
    status: "live",
  },
  {
    path: "/itf-14-label-generator",
    label: "ITF-14 Carton Label Generator",
    description:
      "Generate ITF-14 carton barcodes with bearer bars and an optional GS1-128 batch/expiry secondary. PNG and SVG output.",
    group: "generator",
    status: "live",
  },
  {
    path: "/pdf417-generator",
    label: "PDF417 Generator",
    description:
      "Encode text or structured payloads as a 2D PDF417 stacked barcode — ID cards, boarding passes, shipping labels. Adjustable EC level, columns and row height.",
    group: "generator",
    status: "live",
  },
  {
    path: "/aztec-code-generator",
    label: "Aztec Code Generator",
    description:
      "Encode boarding passes, transit tickets and structured data as a 2D Aztec barcode with adjustable error correction and layer count.",
    group: "generator",
    status: "live",
  },
  {
    path: "/lookup-validators",
    label: "Barcode Lookup & Validators",
    description:
      "Hub for every ReadBarcode lookup and validator — UPC, EAN, ISBN, GTIN, check-digit and type detection.",
    group: "lookup",
    status: "live",
  },
  {
    path: "/upc-lookup",
    label: "UPC Lookup",
    description: "Validate and look up UPC-A barcodes.",
    group: "lookup",
    status: "live",
  },
  {
    path: "/ean-lookup",
    label: "EAN Lookup",
    description: "Validate and look up EAN-8 and EAN-13 barcodes.",
    group: "lookup",
    status: "live",
  },
  {
    path: "/isbn-barcode-reader",
    label: "ISBN Barcode Reader",
    description: "Validate ISBN-10 and ISBN-13 book barcodes.",
    group: "lookup",
    status: "live",
  },
  {
    path: "/gtin-validator",
    label: "GTIN Validator",
    description: "Validate GTIN-8/12/13/14 with check digits.",
    group: "validator",
    status: "live",
  },
  {
    path: "/check-digit-calculator",
    label: "Check Digit Calculator",
    description: "Compute the correct UPC/EAN/GTIN check digit.",
    group: "validator",
    status: "live",
  },
  {
    path: "/barcode-type-detector",
    label: "Barcode Type Detector",
    description: "Detect the likely format of a barcode value.",
    group: "validator",
    status: "live",
  },
  {
    path: "/gs1-inventory",
    label: "GS1 & Inventory Tools",
    description:
      "Hub for GS1 Digital Link decoding, Sunrise 2027 reference, camera-driven inventory and bulk barcode tools.",
    group: "gs1",
    status: "live",
  },
  {
    path: "/gs1-digital-link-decoder",
    label: "GS1 Digital Link Decoder",
    description: "Parse GS1 Digital Link URLs into structured data.",
    group: "gs1",
    status: "live",
  },
  // /sunrise-2027 used to live here as a standalone reference page. The
  // canonical Sunrise 2027 content is now /barcode-guides/sunrise-2027-explained;
  // the old URL is a 301 redirect (see src/routes/sunrise-2027.tsx) and
  // therefore not registered in TOOL_ROUTES — it shouldn't show up in the
  // sitemap, the nav dropdown, or the SSR test sweep.
  {
    path: "/inventory-scanner",
    label: "Inventory Scanner",
    description: "Lightweight barcode-driven inventory list.",
    group: "inventory",
    status: "live",
  },
  {
    path: "/batch-barcode-scanner",
    label: "Batch Barcode Tools",
    description: "Validate and clean many barcodes at once.",
    group: "inventory",
    status: "live",
  },
  {
    path: "/barcode-guides",
    label: "Barcode Guides",
    description: "Plain-English guides to barcode formats and standards.",
    group: "guide",
    status: "live",
  },
  {
    path: "/barcode-guides/upc-vs-ean",
    label: "UPC vs EAN",
    description: "Practical difference between UPC-A and EAN-13.",
    group: "guide",
    status: "live",
  },
  {
    path: "/barcode-guides/what-is-a-gtin",
    label: "What is a GTIN?",
    description: "GTIN-8/12/13/14 explained with check digit basics.",
    group: "guide",
    status: "live",
  },
  {
    path: "/barcode-guides/1d-vs-2d-barcodes",
    label: "1D vs 2D barcodes",
    description: "When to use 1D vs 2D barcodes on packaging.",
    group: "guide",
    status: "live",
  },
  {
    path: "/barcode-guides/code-128-explained",
    label: "Code 128 explained",
    description: "Subsets, FNC1 and GS1-128 in plain English.",
    group: "guide",
    status: "live",
  },
  {
    path: "/barcode-guides/barcode-formats",
    label: "Barcode formats",
    description: "Field guide to common 1D and 2D barcode formats.",
    group: "guide",
    status: "live",
  },
  {
    path: "/barcode-guides/sunrise-2027-explained",
    label: "Sunrise 2027 explained",
    description: "The 2D-at-POS transition: what brands, retailers and consumer packs need to do.",
    group: "guide",
    status: "live",
  },
  {
    path: "/barcode-guides/gs1-digital-link",
    label: "GS1 Digital Link",
    description: "One QR for POS and phone — URL syntax, AI placement and resolvers.",
    group: "guide",
    status: "live",
  },
  {
    path: "/barcode-guides/check-digits-explained",
    label: "Check digits explained",
    description:
      "The GS1 mod-10 algorithm in plain English, plus ISBN-10 mod-11 and Code 128 mod-103.",
    group: "guide",
    status: "live",
  },
];

/** Site-wide informational and legal pages. Included in sitemap and footer. */
export const LEGAL_ROUTES: ToolRoute[] = [
  {
    path: "/about",
    label: "About",
    description: "About ReadBarcode.com and the free barcode toolkit.",
    group: "legal",
    status: "live",
  },
  {
    path: "/contact",
    label: "Contact",
    description: "Bug reports, tool requests, and general feedback.",
    group: "legal",
    status: "live",
  },
  {
    path: "/privacy",
    label: "Privacy",
    description: "How ReadBarcode.com handles your data.",
    group: "legal",
    status: "live",
  },
  {
    path: "/terms",
    label: "Terms",
    description: "Terms of service for the ReadBarcode tools.",
    group: "legal",
    status: "live",
  },
];

/** Flat top-level header links. Kept for parity with the dropdown groups. */
export const PRIMARY_NAV: { path: string; label: string }[] = [];

/** Dropdown groups in the primary nav. */
export const PRIMARY_NAV_GROUPS: {
  label: string;
  /** Optional hub page that the group label links to when present. The
   *  chevron next to the label still opens the dropdown either way. */
  hubPath?: string;
  items: { path: string; label: string; description: string }[];
}[] = [
  {
    label: "Readers",
    hubPath: "/readers",
    items: [
      {
        path: "/barcode-reader",
        label: "Online Barcode Reader",
        description: "Scan from camera or upload to decode any 1D / 2D barcode.",
      },
      {
        path: "/read-barcode-from-image",
        label: "Read Barcode From Image",
        description: "Upload a photo or screenshot to extract the encoded value.",
      },
      {
        path: "/qr-code-reader",
        label: "QR Code Reader",
        description: "Dedicated QR decoder — camera or image.",
      },
      {
        path: "/data-matrix-reader",
        label: "Data Matrix Reader",
        description: "Decode plain and GS1 Data Matrix codes.",
      },
    ],
  },
  {
    label: "Generators",
    hubPath: "/generators",
    items: [
      {
        path: "/barcode-generator",
        label: "Barcode Generator",
        description: "UPC, EAN, Code 128, ITF-14 and more.",
      },
      {
        path: "/bulk-barcode-generator",
        label: "Bulk Barcode Generator",
        description: "Generate many barcodes from a pasted list.",
      },
      {
        path: "/qr-code-generator",
        label: "QR Code Generator",
        description: "QR codes with custom colors and error correction.",
      },
      {
        path: "/wifi-qr-code-generator",
        label: "Wi-Fi QR Code",
        description: "Share your Wi-Fi as a one-scan QR.",
      },
      {
        path: "/gs1-digital-link-generator",
        label: "GS1 Digital Link",
        description: "GTIN + AIs as a Sunrise-2027 URL, QR or Data Matrix.",
      },
      {
        path: "/data-matrix-generator",
        label: "Data Matrix",
        description: "Encode UDI, GS1 or text as a Data Matrix barcode.",
      },
      {
        path: "/vcard-qr-generator",
        label: "vCard QR Code",
        description: "Share contact details as a one-scan QR.",
      },
      {
        path: "/itf-14-label-generator",
        label: "ITF-14 Carton Label",
        description: "Carton/shipping barcode with bearer bars and optional GS1-128.",
      },
      {
        path: "/pdf417-generator",
        label: "PDF417",
        description: "2D stacked barcode for IDs, boarding passes and shipping.",
      },
      {
        path: "/aztec-code-generator",
        label: "Aztec Code",
        description: "2D barcode used on boarding passes and rail tickets.",
      },
    ],
  },
  {
    label: "Lookup & Validators",
    hubPath: "/lookup-validators",
    items: [
      {
        path: "/upc-lookup",
        label: "UPC Lookup",
        description: "Validate and look up UPC-A barcodes.",
      },
      {
        path: "/ean-lookup",
        label: "EAN Lookup",
        description: "Validate and look up EAN-8 and EAN-13 barcodes.",
      },
      {
        path: "/isbn-barcode-reader",
        label: "ISBN Barcode Reader",
        description: "Validate ISBN-10 and ISBN-13 book barcodes.",
      },
      {
        path: "/gtin-validator",
        label: "GTIN Validator",
        description: "Validate GTIN-8/12/13/14 with check digits.",
      },
      {
        path: "/check-digit-calculator",
        label: "Check Digit Calculator",
        description: "Compute the correct UPC/EAN/GTIN check digit.",
      },
      {
        path: "/barcode-type-detector",
        label: "Barcode Type Detector",
        description: "Detect the likely format of a barcode value.",
      },
    ],
  },
  {
    label: "GS1 & Inventory",
    hubPath: "/gs1-inventory",
    items: [
      {
        path: "/gs1-digital-link-decoder",
        label: "GS1 Digital Link Decoder",
        description: "Parse GS1 Digital Link URLs into structured AIs.",
      },
      {
        path: "/inventory-scanner",
        label: "Inventory Scanner",
        description: "Lightweight barcode-driven inventory list with CSV export.",
      },
      {
        path: "/batch-barcode-scanner",
        label: "Batch Barcode Tools",
        description: "Validate, dedupe and clean large lists of barcodes.",
      },
    ],
  },
  {
    label: "Guides",
    hubPath: "/barcode-guides",
    // No dropdown items — Guides renders as a plain link to the guides hub.
    items: [],
  },
];

export const SITE = {
  name: "ReadBarcode",
  domain: "https://readbarcode.com",
  tagline: "Online Barcode Reader, Product Lookup & GS1 Barcode Tools",
};
